import { z } from "zod";

export const LEGACY_CODE_TOPIC = "chickytutor.exercise.code.v1";
export const LEGACY_EVENT_TOPIC = "chickytutor.exercise.events.v1";
export const CODE_TOPIC = "chickytutor.exercise.code.v2";
export const EVENT_TOPIC = "chickytutor.exercise.events.v2";
export const CAPABILITY = "chickytutor.exercise.sdk";
export const SDK_VERSION = "2";
export const MAX_ARTIFACT_BYTES = 96 * 1024;
const id = z
  .string()
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,63}$/)
  .refine((v) => !["constructor", "prototype", "__proto__"].includes(v));
const text = z.string().min(1).max(4000);
export const ActivitySchema = z
  .object({
    id,
    prompt: text,
    mode: z.enum(["text", "choice", "sequence", "open"]),
    options: z.array(z.string().min(1).max(300)).max(20).default([]),
    answers: z.array(z.string().min(1).max(1000)).max(12).default([]),
    hint: z.string().max(1000).default(""),
    explanation: z.string().max(1000).default(""),
  })
  .strict()
  .superRefine((a, ctx) => {
    if (a.mode !== "open" && !a.answers.length)
      ctx.addIssue({ code: "custom", message: "Answer required" });
    if (
      a.mode === "choice" &&
      (a.options.length < 2 || a.answers.some((v) => !a.options.includes(v)))
    )
      ctx.addIssue({ code: "custom", message: "Choice answers must reference options" });
    if (new Set(a.options).size !== a.options.length && a.mode === "choice")
      ctx.addIssue({ code: "custom", message: "Duplicate choices" });
    if (
      a.mode === "sequence" &&
      (!a.options.length || a.answers.some((v) => v !== a.options.join(" ")))
    )
      ctx.addIssue({
        code: "custom",
        message: "Sequence options must be supplied in correct order",
      });
  });
export const PageSchema = z
  .object({
    id,
    title: z.string().min(1).max(60),
    instruction: z.string().min(1).max(180),
    context: z.string().max(900).default(""),
    activities: z.array(id).min(1).max(3),
  })
  .strict();
export const ProgramSchema = z
  .object({
    title: z.string().min(1).max(120),
    objective: z.string().min(1).max(300),
    level: z.enum(["A1", "A2", "B1", "B2", "C1", "C2"]),
    language: z.string().min(1).max(60),
    source: z
      .string()
      .min(1)
      .max(48 * 1024),
    passage: z.string().max(6000).default(""),
    activities: z.array(ActivitySchema).min(1).max(24),
    pages: z.array(PageSchema).min(1).max(5).optional(),
  })
  .strict()
  .superRefine((p, ctx) => {
    if (new TextEncoder().encode(p.source).length > 48 * 1024)
      ctx.addIssue({ code: "custom", message: "Source byte budget exceeded" });
    if (new Set(p.activities.map((a) => a.id)).size !== p.activities.length)
      ctx.addIssue({ code: "custom", message: "Activity IDs must be unique" });
    if (p.pages) {
      const members = p.pages.flatMap((page) => page.activities);
      if (
        new Set(p.pages.map((page) => page.id)).size !== p.pages.length ||
        members.length !== p.activities.length ||
        new Set(members).size !== members.length ||
        members.some((id) => !p.activities.some((a) => a.id === id))
      )
        ctx.addIssue({
          code: "custom",
          message: "Pages must partition all activities exactly once",
        });
      if (p.passage) ctx.addIssue({ code: "custom", message: "Put passages in page context" });
      if (
        p.activities.some(
          (a) =>
            a.prompt.length > 240 ||
            a.hint.length > 240 ||
            a.explanation.length > 300 ||
            a.options.length > 8 ||
            a.options.some((v) => v.length > 100)
        )
      )
        ctx.addIssue({ code: "custom", message: "Page activity content budget exceeded" });
    }
  });
export const ArtifactSchema = z
  .object({
    sdk: z.enum(["1", "2"]),
    session: id,
    id,
    revision: z.number().int().positive(),
    parentRevision: z.number().int().nonnegative(),
    request: z.number().int().nonnegative(),
    hash: z.string().regex(/^[a-f0-9]{64}$/),
    program: ProgramSchema,
  })
  .strict()
  .superRefine((a, ctx) => {
    if ((a.sdk === "2") !== !!a.program.pages)
      ctx.addIssue({ code: "custom", message: "SDK 2 requires pages; SDK 1 is legacy only" });
  });
export type Program = z.infer<typeof ProgramSchema>;
export type Artifact = z.infer<typeof ArtifactSchema>;
export type Activity = z.infer<typeof ActivitySchema>;
export type ExercisePage = z.infer<typeof PageSchema>;
export function currentPage(program: Program, state: LearnerState) {
  return program.pages?.find((page) => page.id === state.viewedPageId) ?? program.pages?.[0];
}
export function pageProgram(program: Program, state: LearnerState) {
  const page = currentPage(program, state);
  return page
    ? {
        ...program,
        page,
        passage: page.context,
        activities: page.activities.map((id) => program.activities.find((a) => a.id === id)!),
      }
    : program;
}
export type LearnerState = {
  viewedPageId?: string;
  pools?: Record<string, number[]>;
  orders: Record<string, number[]>;
  answers: Record<string, string>;
  attempts: Record<string, number>;
  results: Record<string, "correct" | "incorrect" | "pending">;
  hints: Record<string, boolean>;
  view: Record<string, string | number | boolean>;
  sequence: number;
};
export const initialState = (): LearnerState => ({
  orders: {},
  answers: {},
  attempts: {},
  results: {},
  hints: {},
  view: {},
  sequence: 0,
});
export const ReferenceSchema = z
  .object({ session: id, id, revision: z.number().int().positive() })
  .strict();

const tags = [
  "stack",
  "row",
  "grid",
  "panel",
  "text",
  "heading",
  "strong",
  "em",
  "mark",
  "divider",
  "table",
  "tr",
  "td",
  "activity",
  "input",
  "check",
  "hint",
  "feedback",
  "button",
] as const;
export type UINode = {
  tag: (typeof tags)[number];
  props: Record<string, string | number | boolean>;
  children: (UINode | string)[];
};
const Props = z
  .object({
    key: id.optional(),
    activity: id.optional(),
    action: id.optional(),
    label: z.string().max(200).optional(),
    value: z.union([z.string().max(1000), z.number().finite(), z.boolean()]).optional(),
    tone: z.enum(["normal", "muted", "accent", "success", "error"]).optional(),
    size: z.enum(["small", "body", "large"]).optional(),
    columns: z.number().int().min(1).max(3).optional(),
    gap: z.enum(["small", "medium", "large"]).optional(),
  })
  .strict();
export function validateTree(value: unknown, program: Program): UINode {
  let count = 0;
  let textLength = 0;
  const activities = new Set(program.activities.map((a) => a.id));
  const visible = new Set<string>();
  const checkable = new Set<string>();
  const keys = new Set<string>();
  function visit(v: unknown, depth: number): UINode | string {
    if (++count > 500 || depth > 24) throw new Error("Tree budget exceeded");
    if (typeof v === "string") {
      if (v.length > 6000) throw new Error("Text budget exceeded");
      textLength += v.length;
      if (program.pages && textLength > 1600) throw new Error("Page text budget exceeded");
      return v;
    }
    const raw = z
      .object({ tag: z.enum(tags), props: Props, children: z.array(z.unknown()).max(100) })
      .strict()
      .parse(v);
    if (raw.props.key) {
      if (keys.has(raw.props.key)) throw new Error("Duplicate UI key");
      keys.add(raw.props.key);
    }
    if (["activity", "input", "check", "hint", "feedback"].includes(raw.tag)) {
      if (
        !raw.props.activity ||
        !activities.has(raw.props.activity) ||
        (["activity", "input"].includes(raw.tag) && visible.has(raw.props.activity))
      )
        throw new Error("Invalid activity binding");
      if (["activity", "input"].includes(raw.tag)) visible.add(raw.props.activity);
      if (["activity", "check"].includes(raw.tag)) checkable.add(raw.props.activity);
    }
    if (raw.tag === "button" && (!raw.props.action || !raw.props.label))
      throw new Error("Buttons need action and label");
    return {
      tag: raw.tag,
      props: raw.props as UINode["props"],
      children: raw.children.map((c) => visit(c, depth + 1)),
    };
  }
  const root = visit(value, 0);
  if (typeof root === "string") throw new Error("Expected UI root");
  if (!visible.size) throw new Error("Exercise must expose an actionable activity");
  if (program.pages && visible.size !== activities.size)
    throw new Error("Every page activity must remain visible; use host arrows for stages");
  if ([...visible].some((id) => !checkable.has(id)))
    throw new Error("Each visible activity needs a check control");
  return root;
}
export function visibleText(tree: UINode, program: Program, state?: LearnerState): string {
  const parts: string[] = [];
  const described = new Set<string>();
  function walk(n: UINode | string) {
    if (typeof n === "string") {
      parts.push(n);
      return;
    }
    if (n.props.label) parts.push(String(n.props.label));
    if (n.props.activity && !described.has(String(n.props.activity))) {
      const id = String(n.props.activity);
      described.add(id);
      const a = program.activities.find((a) => a.id === id);
      if (a) {
        parts.push(a.prompt);
        if (a.mode === "choice") parts.push(`Choices: ${a.options.join(" | ")}`);
        if (state?.answers[id]) parts.push(`Learner input: ${state.answers[id]}`);
        if (state?.hints[id]) parts.push(`Visible hint: ${a.hint}`);
      }
    }
    n.children.forEach(walk);
  }
  walk(tree);
  return parts.join(" ").slice(0, 4000);
}
